import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import * as XLSX from 'xlsx';

import { getUsuarioStorage } from '../../helpers/getUsuarioStorage';
import { useHideMenu } from '../../hooks/useHideMenu';
import { useGetPantallas } from '../../hooks/useGetPantallas';

import { SelectElement } from "../../components/selectElement2";
import { NoAcceso } from '../../components/NoAcceso';
import LoadingOverlay from '../../components/LoadingOverlay';
import { serverApi } from '../../api';

const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const hoy = formatDate(new Date());

const OPCIONES_POR_PAGINA = [
    { value: 50, label: '50 registros' },
    { value: 100, label: '100 registros' },
    { value: 200, label: '200 registros' },
    { value: 0, label: 'Todos' },
];

export const ListaPacientes = () => {
    const [usuario] = useState(getUsuarioStorage());
    const pantallas = useGetPantallas();
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(false);
    const [atencion, setAtencion] = useState([]);
    const [modulo, setModulo] = useState([]);
    const [agente, setAgente] = useState([]);
    const [pacienteInput, setPacienteInput] = useState('');

    const [listaAtenciones, setListaAtenciones] = useState([]);

    const [fechaInicio, setFechaInicio] = useState(hoy);
    const [fechaFinal, setFechaFinal] = useState(hoy);
    const [filtroTiempo, setFiltroTiempo] = useState('dia');

    const [moduloSeleccionado, setModuloSeleccionado] = useState('');
    const [atencionSeleccionado, setAtencionSeleccionado] = useState('');
    const [agenteSeleccionado, setAgenteSeleccionado] = useState('');

    // Paginación
    const [paginaActual, setPaginaActual] = useState(1);
    const [registrosPorPagina, setRegistrosPorPagina] = useState(50);

    useHideMenu(false);

    useEffect(() => {
        if (usuario.uidUsuario === null) navigate("/Ingreso");
    }, [usuario.uidUsuario, navigate]);

    /* Cargar Tipos de Atención por Entidad y Sede */
    useEffect(() => {
        const codigoEntidad = usuario.codigoEntidad || localStorage.getItem("codigoEntidad");
        const codigoUbicacion = usuario.codigoUbicacion || localStorage.getItem("codigoUbicacion");
        if (!codigoEntidad || !codigoUbicacion) return;

        serverApi.post(`/tipos-atencion/select-tiposatencion`, { codigoEntidad, codigoUbicacion })
            .then(resp => {
                if (resp.data?.lista) {
                    setAtencion(resp.data.lista.map(item => ({
                        value: item.uidTipoAtencion,
                        label: item.tipoAtencion.toUpperCase()
                    })));
                }
            })
            .catch(e => console.error("Error tipos atención:", e));
    }, [usuario]);

    /* Cargar Módulos por Entidad y Sede */
    useEffect(() => {
        const codigoEntidad = usuario.codigoEntidad || localStorage.getItem("codigoEntidad");
        const codigoUbicacion = usuario.codigoUbicacion || localStorage.getItem("codigoUbicacion");
        if (!codigoEntidad || !codigoUbicacion) return;

        serverApi.post(`/modulos/select-modulos`, { codigoEntidad, codigoUbicacion })
            .then(resp => {
                if (resp.data?.listaModulos) {
                    setModulo(resp.data.listaModulos.map(item => {
                        const clase = (item.moduloClase || '').toUpperCase();
                        const mod = (item.modulo || '').toUpperCase();
                        return { value: item.uidModulo, label: clase && mod ? `${clase} - ${mod}` : (clase || mod) };
                    }));
                }
            })
            .catch(e => console.error("Error módulos:", e));
    }, [usuario]);

    /* Cargar Agentes por Entidad y Sede */
    useEffect(() => {
        const codigoEntidad = usuario.codigoEntidad || localStorage.getItem("codigoEntidad");
        const codigoUbicacion = usuario.codigoUbicacion || localStorage.getItem("codigoUbicacion");
        if (!codigoEntidad) return;

        serverApi.post(`/usuarios/select-usuarios`, { codigoEntidad, codigoUbicacion })
            .then(resp => {
                if (resp.data?.data) {
                    setAgente(resp.data.data.map(item => ({
                        value: item.uidUsuario,
                        label: item.nombreCompleto.toUpperCase()
                    })));
                }
            })
            .catch(e => console.error("Error agentes:", e));
    }, [usuario]);

    /* Búsqueda manual */
    const buscarPacientes = useCallback(async (overrideInicio, overrideFinal) => {
        const fInicio = overrideInicio || fechaInicio;
        const fFinal = overrideFinal || fechaFinal;
        if (!fInicio || !fFinal) return;

        const codigoEntidad = usuario.codigoEntidad || localStorage.getItem("codigoEntidad");
        const codigoUbicacion = usuario.codigoUbicacion || localStorage.getItem("codigoUbicacion");

        try {
            setIsLoading(true);
            const resp = await serverApi.post(`/informes/lista-pacientes`, {
                codigoEntidad, codigoUbicacion,
                fechaInicio: fInicio, fechaFinal: fFinal,
                modulo: moduloSeleccionado, atencion: atencionSeleccionado,
                uidAgente: agenteSeleccionado, paciente: pacienteInput.trim()
            });
            if (resp.data) {
                const dataLista = resp.data.lista || resp.data.listaPacientes || (Array.isArray(resp.data) ? resp.data : []);
                setListaAtenciones(Array.isArray(dataLista) ? dataLista : []);
                setPaginaActual(1); // Volver a página 1 con nueva búsqueda
            }
        } catch (error) {
            console.error("Error al buscar lista de pacientes:", error);
            setListaAtenciones([]);
        } finally {
            setIsLoading(false);
        }
    }, [fechaInicio, fechaFinal, moduloSeleccionado, atencionSeleccionado, agenteSeleccionado, pacienteInput, usuario]);

    /* Carga inicial */
    useEffect(() => {
        const codigoEntidad = usuario.codigoEntidad || localStorage.getItem("codigoEntidad");
        if (codigoEntidad) buscarPacientes();
    }, [usuario.codigoEntidad, buscarPacientes]);

    /* Presets */
    const handlePresetDia = () => { const t = formatDate(new Date()); setFechaInicio(t); setFechaFinal(t); setFiltroTiempo('dia'); buscarPacientes(t, t); };
    const handlePresetMes = () => { const now = new Date(); const f = formatDate(new Date(now.getFullYear(), now.getMonth(), 1)); const l = formatDate(new Date(now.getFullYear(), now.getMonth() + 1, 0)); setFechaInicio(f); setFechaFinal(l); setFiltroTiempo('mes'); buscarPacientes(f, l); };
    const handlePresetAnio = () => { const now = new Date(); const f = formatDate(new Date(now.getFullYear(), 0, 1)); const l = formatDate(new Date(now.getFullYear(), 11, 31)); setFechaInicio(f); setFechaFinal(l); setFiltroTiempo('anio'); buscarPacientes(f, l); };

    /* Paginación calculada */
    const totalRegistros = listaAtenciones.length;
    const mostrarTodos = registrosPorPagina === 0;
    const totalPaginas = mostrarTodos ? 1 : Math.ceil(totalRegistros / registrosPorPagina);
    const registrosMostrados = mostrarTodos
        ? listaAtenciones
        : listaAtenciones.slice((paginaActual - 1) * registrosPorPagina, paginaActual * registrosPorPagina);

    const irPagina = (n) => setPaginaActual(Math.min(Math.max(1, n), totalPaginas));

    /* Render estado */
    const renderEstado = (estado, atendidoEn) => {
        const mapa = {
            'ESPERA': { cls: 'warning', txt: 'En Espera', dark: true },
            'LLAMADO': { cls: 'primary', txt: 'Llamando', dark: false },
            'ATENDIENDO': { cls: 'info', txt: 'Atendiendo', dark: true },
            'FINALIZADO': atendidoEn ? { cls: 'success', txt: 'Atendido', dark: false } : { cls: 'danger', txt: 'No Asistió', dark: false },
            'NO RESPONDIO': { cls: 'danger', txt: 'No Respondió', dark: false },
            'CANCELADO': { cls: 'dark', txt: 'Cancelado', dark: false },
            'PAUSADO': { cls: 'warning', txt: 'Pausado', dark: true },
            'RECLASIFICADO': { cls: 'secondary', txt: 'Reclasificado', dark: false },
        };
        const { cls = 'secondary', txt = estado, dark = false } = mapa[estado] || {};
        return (
            <span className={`badge bg-${cls} ${dark ? 'text-dark' : 'text-white'}`}
                style={{ fontSize: '0.73rem', padding: '4px 10px', borderRadius: 20, fontWeight: 600, whiteSpace: 'nowrap' }}>
                {txt}
            </span>
        );
    };

    /* Exportar Excel */
    const exportToExcel = () => {
        const dataToExport = listaAtenciones.map(item => {
            const cls = (item.moduloClase || '').toUpperCase();
            const mod = (item.modulo || '').toUpperCase();
            return {
                "Paciente": item.nombrePaciente?.trim() || '-',
                "Documento": item.numeroDocumento || '-',
                "Atención": item.tipoAtencion || '',
                "Ticket": item.codigoTipoAtencion ? `${item.codigoTipoAtencion}-${item.numeroTicket}` : item.numeroTicket,
                "Módulo": cls && mod ? `${cls} - ${mod}` : (cls || mod || '-'),
                "Atendió": item.agente || "-",
                "Creado": item.createdAt ? moment(item.createdAt).format('YYYY-MM-DD hh:mm:ss a') : '',
                "Llamado": item.llamadoEn ? moment(item.llamadoEn).format('YYYY-MM-DD hh:mm:ss a') : '-',
                "Atendido": item.atendidoEn ? moment(item.atendidoEn).format('YYYY-MM-DD hh:mm:ss a') : '-',
                "T. Llamado": item.tiempoLlamado || '-',
                "T. Atención": item.tiempoAtencion || '-',
                "Llamados": item.numeroLlamados || 0,
                "Estado": item.estado || ''
            };
        });
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(dataToExport);
        XLSX.utils.book_append_sheet(wb, ws, "Lista_Pacientes");
        XLSX.writeFile(wb, `Lista_Pacientes_${fechaInicio}_al_${fechaFinal}.xlsx`);
    };

    /* Celda de fecha apilada */
    const FechaCell = ({ valor }) => {
        if (!valor) return <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>—</span>;
        return (
            <div style={{ lineHeight: 1.3 }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#334155' }}>{moment(valor).format('DD/MM/YYYY')}</div>
                <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{moment(valor).format('hh:mm a')}</div>
            </div>
        );
    };

    /* Celda de tiempo con badge */
    const TiempoCell = ({ valor, color }) => (
        <span style={{
            display: 'inline-block',
            fontFamily: 'monospace',
            fontSize: '0.78rem',
            fontWeight: 700,
            color: color,
            background: color === '#2563eb' ? '#eff6ff' : '#f0fdf4',
            border: `1px solid ${color === '#2563eb' ? '#bfdbfe' : '#bbf7d0'}`,
            borderRadius: 6,
            padding: '2px 8px',
            whiteSpace: 'nowrap',
        }}>
            {valor || '—'}
        </span>
    );

    if (usuario.uidUsuario === null) return <></>;
    if (!pantallas.includes("listapacientes")) return <NoAcceso />;

    /* Estilos de encabezado de tabla */
    const thStyle = {
        background: '#f8fafc',
        color: '#475569',
        fontWeight: 700,
        fontSize: '0.73rem',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        padding: '10px 12px',
        borderBottom: '2px solid #e2e8f0',
        whiteSpace: 'nowrap',
        verticalAlign: 'middle',
    };
    const tdStyle = {
        padding: '10px 12px',
        verticalAlign: 'middle',
        borderBottom: '1px solid #f1f5f9',
    };

    return (
        <div style={{ minHeight: '100vh', background: '#f8fafc', padding: '24px 20px' }}>
            {isLoading && <LoadingOverlay />}

            {/* CABECERA */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
                <div>
                    <h2 style={{ fontWeight: 800, color: '#0f172a', margin: 0, fontSize: '1.5rem' }}>
                        Lista de Pacientes / Atenciones
                    </h2>
                    <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                        Consulta detallada de flujos de atención y tiempos por paciente.
                    </p>
                </div>

                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span className="badge px-3 py-2 rounded-pill fw-bold" style={{ fontSize: 13, background: '#eff6ff', color: '#2563eb' }}>
                        {totalRegistros} Registro{totalRegistros !== 1 ? 's' : ''}
                    </span>
                    <button
                        className="btn btn-success fw-semibold d-flex align-items-center gap-2 px-3"
                        style={{ height: 38, fontSize: '0.85rem', borderRadius: 10 }}
                        onClick={exportToExcel}
                    >
                        <i className="fa-solid fa-file-excel"></i> Exportar
                    </button>
                </div>
            </div>

            {/* PANEL DE FILTROS */}
            <div className="bg-white rounded-4 shadow-sm border p-4 mb-4">
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <h6 className="fw-bold mb-0" style={{ letterSpacing: 0.5, color: '#1e293b', fontSize: '0.8rem', textTransform: 'uppercase' }}>
                        Filtros de búsqueda
                    </h6>
                    <button
                        className="btn btn-primary fw-bold px-4 d-flex align-items-center gap-2"
                        style={{ height: 36, fontSize: '0.83rem', borderRadius: 10 }}
                        onClick={() => buscarPacientes()}
                    >
                        <i className="fa-solid fa-arrows-rotate"></i> Buscar
                    </button>
                </div>

                <div className="d-flex gap-2 mb-3">
                    {[
                        { key: 'anio', label: 'Año', fn: handlePresetAnio },
                        { key: 'mes', label: 'Mes', fn: handlePresetMes },
                        { key: 'dia', label: 'Día', fn: handlePresetDia },
                    ].map(p => (
                        <button key={p.key} onClick={p.fn}
                            className={`btn btn-sm rounded-pill px-3 fw-semibold ${filtroTiempo === p.key ? 'btn-primary' : 'btn-outline-primary'}`}
                            style={{ fontSize: '0.8rem' }}>
                            {p.label}
                        </button>
                    ))}
                </div>

                <div className="row g-3 align-items-end">
                    <div className="col-12 col-sm-6 col-lg-4 col-xl-2">
                        <label className="form-label fw-bold text-secondary mb-1" style={{ fontSize: '0.75rem' }}>Inicio</label>
                        <input type="date" className="form-control rounded-3" value={fechaInicio}
                            onChange={ev => { setFechaInicio(ev.target.value); if (fechaFinal < ev.target.value) setFechaFinal(ev.target.value); setFiltroTiempo('custom'); }}
                            style={{ fontSize: '0.83rem', height: 36 }} />
                    </div>
                    <div className="col-12 col-sm-6 col-lg-4 col-xl-2">
                        <label className="form-label fw-bold text-secondary mb-1" style={{ fontSize: '0.75rem' }}>Final</label>
                        <input type="date" className="form-control rounded-3" value={fechaFinal} min={fechaInicio}
                            onChange={ev => { setFechaFinal(ev.target.value); setFiltroTiempo('custom'); }}
                            style={{ fontSize: '0.83rem', height: 36 }} />
                    </div>
                    <div className="col-12 col-sm-6 col-lg-4 col-xl-2">
                        <label className="form-label fw-bold text-secondary mb-1" style={{ fontSize: '0.75rem' }}>Tipos Atención</label>
                        <SelectElement options={atencion} value={atencion.find(o => o.value === atencionSeleccionado) || null}
                            isMulti={false} closeOnMenuSelect={true} isClearable={true}
                            onChange={sel => setAtencionSeleccionado(sel ? sel.value : '')} />
                    </div>
                    <div className="col-12 col-sm-6 col-lg-4 col-xl-2">
                        <label className="form-label fw-bold text-secondary mb-1" style={{ fontSize: '0.75rem' }}>Módulos</label>
                        <SelectElement options={modulo} value={modulo.find(o => o.value === moduloSeleccionado) || null}
                            isMulti={false} closeOnMenuSelect={true} isClearable={true}
                            onChange={sel => setModuloSeleccionado(sel ? sel.value : '')} />
                    </div>
                    <div className="col-12 col-sm-6 col-lg-4 col-xl-2">
                        <label className="form-label fw-bold text-secondary mb-1" style={{ fontSize: '0.75rem' }}>Agentes</label>
                        <SelectElement options={agente} value={agente.find(o => o.value === agenteSeleccionado) || null}
                            isMulti={false} closeOnMenuSelect={true} isClearable={true}
                            onChange={sel => setAgenteSeleccionado(sel ? sel.value : '')} />
                    </div>
                    <div className="col-12 col-sm-6 col-lg-4 col-xl-2">
                        <label className="form-label fw-bold text-secondary mb-1" style={{ fontSize: '0.75rem' }}>Paciente</label>
                        <input type="text" placeholder="Documento o Nombre" className="form-control rounded-3"
                            value={pacienteInput}
                            onChange={ev => setPacienteInput(ev.target.value)}
                            onKeyDown={ev => { if (ev.key === 'Enter') buscarPacientes(); }}
                            style={{ fontSize: '0.83rem', height: 36 }} />
                    </div>
                </div>
            </div>

            {/* TOOLBAR PAGINACIÓN */}
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                <div className="d-flex align-items-center gap-2">
                    <span className="text-secondary fw-semibold" style={{ fontSize: '0.82rem' }}>Mostrar:</span>
                    {OPCIONES_POR_PAGINA.map(op => (
                        <button key={op.value}
                            onClick={() => { setRegistrosPorPagina(op.value); setPaginaActual(1); }}
                            className={`btn btn-sm ${registrosPorPagina === op.value ? 'btn-primary' : 'btn-outline-secondary'} fw-semibold`}
                            style={{ fontSize: '0.78rem', borderRadius: 8, padding: '3px 12px' }}>
                            {op.label}
                        </button>
                    ))}
                </div>
                {!mostrarTodos && totalPaginas > 1 && (
                    <div className="d-flex align-items-center gap-1">
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => irPagina(1)} disabled={paginaActual === 1} style={{ borderRadius: 8, fontSize: '0.8rem' }}>«</button>
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => irPagina(paginaActual - 1)} disabled={paginaActual === 1} style={{ borderRadius: 8, fontSize: '0.8rem' }}>‹</button>
                        <span className="px-3 fw-semibold" style={{ fontSize: '0.82rem', color: '#475569' }}>
                            Pág. {paginaActual} de {totalPaginas}
                        </span>
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => irPagina(paginaActual + 1)} disabled={paginaActual === totalPaginas} style={{ borderRadius: 8, fontSize: '0.8rem' }}>›</button>
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => irPagina(totalPaginas)} disabled={paginaActual === totalPaginas} style={{ borderRadius: 8, fontSize: '0.8rem' }}>»</button>
                    </div>
                )}
                <span className="badge px-3 py-2 rounded-pill fw-bold" style={{ fontSize: 13, background: '#eff6ff', color: '#2563eb' }}>
                    {totalRegistros} Registro{totalRegistros !== 1 ? 's' : ''}
                </span>
            </div>

            {/* TABLA */}
            <div className="bg-white rounded-4 shadow-sm border overflow-hidden">
                <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 360px)' }}>
                    <table style={{ borderCollapse: 'collapse', tableLayout: 'auto', minWidth: '100%' }}>
                        <thead>
                            <tr>
                                <th style={{ ...thStyle, }}>Paciente</th>
                                <th style={{ ...thStyle, }}>Atención</th>
                                <th style={{ ...thStyle, textAlign: 'center' }}>Ticket</th>
                                <th style={{ ...thStyle, }}>Módulo</th>
                                <th style={{ ...thStyle, }}>Atendió</th>
                                <th style={{ ...thStyle, }}>Creado</th>
                                <th style={{ ...thStyle, }}>Llamado</th>
                                <th style={{ ...thStyle, }}>Atendido</th>
                                <th style={{ ...thStyle, textAlign: 'center' }}>T. Llamado</th>
                                <th style={{ ...thStyle, textAlign: 'center' }}>T. Atención</th>
                                <th style={{ ...thStyle, textAlign: 'center' }}># Llamados</th>
                                <th style={{ ...thStyle, textAlign: 'center' }}>Estado</th>
                            </tr>
                        </thead>
                        <tbody>
                            {registrosMostrados.length === 0 ? (
                                <tr>
                                    <td colSpan="12" style={{ ...tdStyle, textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
                                        <i className="fa-solid fa-users-slash" style={{ fontSize: 36, display: 'block', marginBottom: 12 }}></i>
                                        No se encontraron registros para los filtros seleccionados
                                    </td>
                                </tr>
                            ) : (
                                registrosMostrados.map((item, i) => {
                                    const modClase = (item.moduloClase || '').toUpperCase();
                                    const modNom = (item.modulo || '').toUpperCase();
                                    const moduloDisplay = modClase && modNom ? `${modClase} - ${modNom}` : (modClase || modNom || '—');
                                    const nombrePac = item.nombrePaciente?.trim() || '—';
                                    const ticketLabel = item.codigoTipoAtencion
                                        ? `${item.codigoTipoAtencion}-${item.numeroTicket}`
                                        : item.numeroTicket;

                                    return (
                                        <tr key={i} style={{ background: i % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                                            {/* Paciente + Documento */}
                                            <td style={tdStyle}>
                                                <div style={{ fontWeight: 700, fontSize: '0.78rem', color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={nombrePac}>
                                                    {nombrePac.toUpperCase()}
                                                </div>
                                                {item.numeroDocumento && (
                                                    <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 2 }}>
                                                        {/* <i className="fa-regular fa-id-card" style={{ marginRight: 3 }}></i> */}
                                                        {item.numeroDocumento}
                                                    </div>
                                                )}
                                            </td>

                                            {/* Tipo Atención */}
                                            <td style={tdStyle}>
                                                <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.tipoAtencion}>
                                                    {(item.tipoAtencion || '—').toUpperCase()}
                                                </div>
                                            </td>

                                            {/* Ticket */}
                                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                <span style={{
                                                    fontFamily: 'monospace',
                                                    fontSize: '0.78rem',
                                                    fontWeight: 700,
                                                    color: '#1e293b',
                                                    background: '#f1f5f9',
                                                    border: '1px solid #cbd5e1',
                                                    borderRadius: 6,
                                                    padding: '2px 7px',
                                                    whiteSpace: 'nowrap',
                                                }}>
                                                    {ticketLabel}
                                                </span>
                                            </td>

                                            {/* Módulo */}
                                            <td style={tdStyle}>
                                                <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={moduloDisplay}>
                                                    {moduloDisplay}
                                                </div>
                                            </td>

                                            {/* Agente */}
                                            <td style={tdStyle}>
                                                <div style={{ fontSize: '0.78rem', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.agente}>
                                                    {item.agente ? item.agente.toUpperCase() : '—'}
                                                </div>
                                            </td>

                                            {/* Fechas */}
                                            <td style={tdStyle}><FechaCell valor={item.createdAt} /></td>
                                            <td style={tdStyle}><FechaCell valor={item.llamadoEn} /></td>
                                            <td style={tdStyle}><FechaCell valor={item.atendidoEn} /></td>

                                            {/* Tiempos */}
                                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                <TiempoCell valor={item.tiempoLlamado} color="#2563eb" />
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                <TiempoCell valor={item.tiempoAtencion} color="#16a34a" />
                                            </td>

                                            {/* N° Llamados */}
                                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                <span style={{ fontWeight: 700, fontSize: '0.8rem', color: '#475569' }}>
                                                    {item.numeroLlamados || 0}
                                                </span>
                                            </td>

                                            {/* Estado */}
                                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                {renderEstado(item.estado, item.atendidoEn)}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Footer de paginación */}
                {!mostrarTodos && totalPaginas > 1 && (
                    <div style={{ padding: '12px 20px', borderTop: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc' }}>
                        <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
                            Mostrando {(paginaActual - 1) * registrosPorPagina + 1}–{Math.min(paginaActual * registrosPorPagina, totalRegistros)} de {totalRegistros} registros
                        </span>
                        <div className="d-flex align-items-center gap-1">
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => irPagina(1)} disabled={paginaActual === 1} style={{ borderRadius: 8, fontSize: '0.8rem' }}>«</button>
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => irPagina(paginaActual - 1)} disabled={paginaActual === 1} style={{ borderRadius: 8, fontSize: '0.8rem' }}>‹</button>
                            <span className="px-2" style={{ fontSize: '0.82rem', fontWeight: 600, color: '#334155' }}>
                                Pág. {paginaActual} / {totalPaginas}
                            </span>
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => irPagina(paginaActual + 1)} disabled={paginaActual === totalPaginas} style={{ borderRadius: 8, fontSize: '0.8rem' }}>›</button>
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => irPagina(totalPaginas)} disabled={paginaActual === totalPaginas} style={{ borderRadius: 8, fontSize: '0.8rem' }}>»</button>
                        </div>
                    </div>
                )}
            </div>

        </div>
    );
};
